import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { circlePeri, percentPeri } from './doughnuts-inner-utils';

export const CLASSROOM = 0;
export const GROUP = 1;
export const STUDENT = 2;
export const MOUSE_STATE_ENTER = 'MOUSE_STATE_ENTER';
export const MOUSE_STATE_LEAVE = 'MOUSE_STATE_LEAVE';
export const MOUSE_STATE_CLICK = 'MOUSE_STATE_CLICK';

const xPosCommon = 80;
const yPosCommon = 80;
const radiusCommon = 70;
const colorCommon = '#e8f3f3';
const widthCommon = 12;
const fillCommon = 'transparent';
const dashArrayCommon = circlePeri(radiusCommon);
const dashOffsetCommon = 0;
const lineCapCommon = 'round';

export const classColor = '#c5e0e1';
export const groupColor = '#c6d4e1';
export const studentColor = '#088bd3';
export const doughnutWidth = widthCommon;
export const doughnutGap = 6;

const RawDoughnut = ({
  xPos = xPosCommon,
  yPos = yPosCommon,
  radius = radiusCommon,
  color = colorCommon,
  width = widthCommon,
  fill = fillCommon,
  dashArray = dashArrayCommon,
  dashOffset = dashOffsetCommon,
  lineCap = lineCapCommon,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onClick = () => {},
  pointer
}) => (
  <circle
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    transform={`rotate(270 ${xPos} ${yPos})`}
    cx={xPos}
    cy={yPos}
    r={radius}
    stroke={color}
    strokeWidth={width}
    fill={fill}
    strokeDasharray={dashArray}
    strokeDashoffset={dashOffset}
    strokeLinecap={lineCap}
    style={{ cursor: pointer ? 'pointer' : 'default' }}
  />
);

RawDoughnut.propTypes = {
  xPos: PropTypes.number,
  yPos: PropTypes.number,
  radius: PropTypes.number,
  color: PropTypes.string,
  width: PropTypes.number,
  fill: PropTypes.string,
  dashArray: PropTypes.number,
  dashOffset: PropTypes.number,
  lineCap: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  pointer: PropTypes.bool
};

export const useClassroomDoughnuts = ({ resultType = CLASSROOM, classroomPct, groupPct, studentPct }) => {
  const gap = resultType === STUDENT ? (doughnutGap / 2) : doughnutGap;
  const groupRadius = radiusCommon - widthCommon - gap;
  const groupDashArray = circlePeri(groupRadius);

  const studentRadius = groupRadius - widthCommon - gap;
  const studentDashArray = circlePeri(studentRadius);

  const [doughnutData, setDoughnutData] = useState({
    doughnutType: CLASSROOM,
    mouseState: MOUSE_STATE_LEAVE
  });

  const updateMouseState = ({ doughnutType, mouseState }) => {
    if (doughnutData.doughnutType !== doughnutType || doughnutData.mouseState !== mouseState) {
      setDoughnutData({ doughnutType, mouseState });
    }
  };

  const ClassDoughnuts = () => (
    <Fragment>
      <RawDoughnut
        onMouseEnter={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      />
      { classroomPct > 0 && <RawDoughnut
        color={classColor}
        dashOffset={percentPeri(radiusCommon, classroomPct)}
        onMouseEnter={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: CLASSROOM,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      /> }
    </Fragment>
  );

  const GroupDoughnuts = () => (
    <Fragment>
      <RawDoughnut
        radius={groupRadius}
        dashArray={groupDashArray}
        onMouseEnter={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      />
      { groupPct > 0 && <RawDoughnut
        color={groupColor}
        radius={groupRadius}
        dashArray={groupDashArray}
        dashOffset={percentPeri(groupRadius, groupPct)}
        onMouseEnter={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: GROUP,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      /> }
    </Fragment>
  );

  const StudentDoughnuts = () => (
    <Fragment>
      <RawDoughnut
        radius={studentRadius}
        dashArray={studentDashArray}
        onMouseEnter={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      />
      { studentPct > 0 && <RawDoughnut
        color={studentColor}
        radius={studentRadius}
        dashArray={studentDashArray}
        dashOffset={percentPeri(studentRadius, studentPct)}
        onMouseEnter={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_ENTER
        })}
        onMouseLeave={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_LEAVE
        })}
        onClick={() => updateMouseState({
          doughnutType: STUDENT,
          mouseState: MOUSE_STATE_CLICK
        })}
        pointer
      /> }
    </Fragment>
  );

  const entities = [
    (
      <ClassDoughnuts />
    ),
    (
      <Fragment>
        <ClassDoughnuts />
        <GroupDoughnuts />
      </Fragment>
    ),
    (
      <Fragment>
        <ClassDoughnuts />
        <GroupDoughnuts />
        <StudentDoughnuts />
      </Fragment>
    )
  ];

  const AllDoughnuts = () => entities[resultType];

  return [AllDoughnuts, doughnutData];
};
